import React, { useMemo, useState } from 'react';
import { Box, AppBar, Toolbar, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, ListSubheader, Divider, IconButton } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Logo from '../components/branding/Logo';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link, Outlet, useLocation } from 'react-router-dom';
import ProfileMenu from '../components/ProfileMenu';

const drawerWidth = 240;

const POS_PORTAL_URL = (import.meta.env.VITE_POS_PORTAL_URL || 'https://pos.suuq-s.com').replace(/\/+$/, '');
const SHOP_URL = (import.meta.env.VITE_PUBLIC_BASE_URL || 'https://suuq-s.com').replace(/\/+$/, '');

const NAV_GROUPS = [
  {
    title: 'Suuq POS',
    items: [
      { text: 'Overview', icon: <DashboardIcon />, path: '/overview' },
      { text: 'Branches', icon: <StorefrontIcon />, path: '/branches' },
      { text: 'Workspaces & Billing', icon: <WorkspacesIcon />, path: '/workspaces' },
      { text: 'Suppliers', icon: <LocalShippingIcon />, path: '/suppliers' },
      { text: 'Equity Partners', icon: <HandshakeIcon />, path: '/equity-partners' },
    ],
  },
  {
    title: 'People & Records',
    items: [
      { text: 'Accounts', icon: <PeopleIcon />, path: '/accounts' },
      { text: 'Audit Log', icon: <HistoryIcon />, path: '/audit' },
    ],
  },
];

const EXTERNAL_LINKS = [
  { text: 'pos.suuq-s.com', href: POS_PORTAL_URL },
  { text: 'suuq-s.com', href: SHOP_URL },
];

function isActivePath(pathname, path) {
  return pathname === path || pathname.startsWith(`${path}/`);
}

export default function MainLayout() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const navGroups = NAV_GROUPS;
  const drawerVariant = isMobile ? 'temporary' : 'permanent';
  const drawerOpen = isMobile ? mobileDrawerOpen : true;
  const activeNavTitle = useMemo(() => {
    for (const group of navGroups) {
      const match = group.items.find((item) => isActivePath(location.pathname, item.path));
      if (match) return match.text;
    }
    return 'Admin';
  }, [location.pathname, navGroups]);

  const closeMobileDrawer = () => {
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={isMobile ? 1 : 0}
        sx={{
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          color: 'text.primary',
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton edge="start" color="inherit" onClick={() => setMobileDrawerOpen(true)} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            <Logo size="md" clickable />
            <Box minWidth={0}>
              <Typography variant="h6" noWrap component="div">Suuq POS Admin</Typography>
              {isMobile && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {activeNavTitle}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <ProfileMenu />
        </Toolbar>
      </AppBar>
      <Drawer
          variant={drawerVariant}
          open={drawerOpen}
          onClose={closeMobileDrawer}
          ModalProps={{ keepMounted: true }}
          sx={{
            width: isMobile ? 0 : drawerWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: isMobile ? Math.min(drawerWidth + 32, 300) : drawerWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          <Toolbar />
          <Box sx={{ overflow: 'auto' }}>
            <List>
              {navGroups.map((group, index) => (
                <React.Fragment key={group.title}>
                  {index > 0 && <Divider />}
                  <ListSubheader disableSticky sx={{ bgcolor: 'transparent', lineHeight: '32px', pt: 2, fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                    {group.title}
                  </ListSubheader>
                  {group.items.map((item) => (
                    <ListItem key={item.text} disablePadding>
                      <ListItemButton
                        component={Link}
                        to={item.path}
                        onClick={closeMobileDrawer}
                        selected={isActivePath(location.pathname, item.path)}
                        sx={{
                          '&.Mui-selected': { bgcolor: 'primary.light', color: 'primary.main', '& .MuiListItemIcon-root': { color: 'primary.main' } },
                          '&:hover': { bgcolor: 'action.hover' },
                          py: 0.5
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </React.Fragment>
              ))}
            </List>
            <Divider />
            <List dense>
              <ListSubheader disableSticky sx={{ bgcolor: 'transparent', lineHeight: '32px', pt: 1, fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                Live sites
              </ListSubheader>
              {EXTERNAL_LINKS.map((link) => (
                <ListItem key={link.href} disablePadding>
                  <ListItemButton component="a" href={link.href} target="_blank" rel="noopener" sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}><OpenInNewIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary={link.text} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          minWidth: 0,
          bgcolor: 'background.default',
          px: { xs: 1.5, sm: 2, md: 3 },
          pb: { xs: 2, md: 3 },
          pl: { xs: 1.5, sm: 2, md: 0 },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}
